import { motion } from "framer-motion";

const featured = {
  metric: "Reuse",
  rest: "your best answers, automatically",
  body: "Every winning answer becomes a reusable asset. Nothing gets lost. Nothing gets rewritten from scratch.",
};

const supporting = [
  {
    metric: "Draft",
    rest: "in minutes, not days",
    body: "Upload a tender, RFP, or RFQ and get a structured first draft built from your own content — ready to refine.",
  },
  {
    metric: "Learns",
    rest: "from every win",
    body: "Mark a proposal as won and Sorvaine learns from it. Your next draft pulls from proven winners — not just everything in your vault. The more you win, the smarter it gets.",
  },
];

export function ProvenResultsSection() {
  return (
    <section className="border-t border-white/[0.05] pt-[96px] md:pt-[112px] lg:pt-[128px] pb-[96px] md:pb-[112px] lg:pb-[128px]">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <span className="text-sm font-normal tracking-[0.12em] uppercase text-muted-foreground mb-8 block">
            Built to Win
          </span>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight leading-[1.15]">
            AI that gets smarter
            <br />
            <span className="text-white/[0.60] font-normal">every time you win.</span>
          </h2>

          <p className="mt-8 text-base md:text-lg font-normal text-white/[0.65] max-w-xl mx-auto leading-[1.75]">
            Every feature is built around one idea: your AI gets better the more you use it — so your tenth proposal is sharper than your first.
          </p>
        </motion.div>

        {/* Asymmetric Layout: Hero stat + supporting pair */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {/* Hero stat — spans 3 columns */}
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-3 group relative landing-card border border-white/[0.08] rounded-lg p-7 md:p-12 lg:p-14 flex flex-col justify-center transition-all duration-200 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_20px_-4px_hsl(var(--primary)/0.15)]"
          >
            <h3 className="text-2xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-4 leading-[1.15]">
              <span className="text-primary font-bold">{featured.metric}</span>{" "}
              {featured.rest}
            </h3>
            <p className="text-base md:text-lg font-normal text-white/[0.65] leading-[1.7] max-w-lg">
              {featured.body}
            </p>
          </motion.div>

          {/* Supporting pair — stacked in 2 columns */}
          <div className="md:col-span-2 flex flex-col gap-6">
            {supporting.map((r, i) => (
              <motion.div
                key={r.metric}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="flex-1 group relative landing-card border border-white/[0.08] rounded-lg p-8 transition-all duration-200 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-[0_0_20px_-4px_hsl(var(--primary)/0.15)]"
              >
                <h3 className="text-lg md:text-xl font-semibold text-white tracking-tight mb-3 leading-[1.25]">
                  <span className="text-primary font-bold">{r.metric}</span>{" "}
                  {r.rest}
                </h3>
                <p className="text-sm font-normal text-white/[0.65] leading-[1.7]">
                  {r.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Editorial pull-quote */}
        <motion.p
          initial={false}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-xl md:text-2xl italic text-white/[0.35] text-center mt-16 max-w-2xl mx-auto leading-relaxed font-light"
        >
          The longer you use Sorvaine, the harder it becomes to go back to starting from scratch.
        </motion.p>
      </div>
    </section>
  );
}
